import { Controller } from "@hotwired/stimulus"

// Cross-property blackout painting for the steward occupancy overview (V2 of
// docs/specs/steward-multi-property-calendar.md). The grid stays read-only
// until the "Block dates" toggle arms paint mode; then dragging a rectangle
// across day cells POSTs mode=block to each touched property's EXISTING
// /properties/:id/set_availability endpoint — one request per property, no
// calendar-specific write path — and reloads so the server-rendered grid stays
// the single source of truth. Undo lives where it always has: the property's
// availability editor (unblock/clear).
export default class extends Controller {
  static targets = ["grid", "toggle", "status"]
  static values = { applyUrls: Object } // { property_id: set_availability path }

  connect() {
    this.painting = false
    this.dragging = false
    this.anchor = null
    this.head = null

    this.gridTarget.addEventListener("mousedown", this.#onDown)
    this.gridTarget.addEventListener("mouseover", this.#onOver)
    document.addEventListener("mouseup", this.#onUp)
  }

  disconnect() {
    this.gridTarget.removeEventListener("mousedown", this.#onDown)
    this.gridTarget.removeEventListener("mouseover", this.#onOver)
    document.removeEventListener("mouseup", this.#onUp)
  }

  // Explicit arming keeps V1's read-only behavior the default — an accidental
  // drag over the grid never mutates data.
  toggle() {
    this.painting = !this.painting
    this.toggleTarget.classList.toggle("active", this.painting)
    this.toggleTarget.setAttribute("aria-pressed", String(this.painting))
    this.element.classList.toggle("wl-occ-painting", this.painting)
    this.#status(this.painting
      ? "Drag across a row to black out dates. To undo, use that property's availability editor."
      : "")
    if (!this.painting) {
      this.dragging = false
      this.anchor = this.head = null
      this.#paintSelection()
    }
  }

  // --- selection (rectangle: date span x property rows) -------------------
  #onDown = (event) => {
    if (!this.painting) return
    const cell = event.target.closest(".wl-occ-day[data-date]")
    if (!cell) return
    event.preventDefault()
    this.dragging = true
    this.anchor = this.head = cell
    this.#paintSelection()
  }

  #onOver = (event) => {
    if (!this.dragging) return
    const cell = event.target.closest(".wl-occ-day[data-date]")
    if (!cell) return
    this.head = cell
    this.#paintSelection()
  }

  #onUp = () => {
    if (!this.dragging) return
    this.dragging = false
    const selection = this.#selection()
    this.anchor = this.head = null
    if (selection) this.#block(selection)
  }

  #selection() {
    if (!this.anchor || !this.head) return null
    const [startDate, endDate] = [this.anchor.dataset.date, this.head.dataset.date].sort()
    const propertyIds = [...new Set(
      this.#selectedCells().map((cell) => cell.dataset.propertyId)
    )]
    return propertyIds.length ? { startDate, endDate, propertyIds } : null
  }

  #selectedCells() {
    if (!this.anchor || !this.head) return []
    const [s, e] = [this.anchor.dataset.date, this.head.dataset.date].sort()
    const [r1, r2] = [Number(this.anchor.dataset.row), Number(this.head.dataset.row)].sort((a, b) => a - b)
    return [...this.gridTarget.querySelectorAll(".wl-occ-day[data-date]")].filter((cell) => {
      const row = Number(cell.dataset.row)
      return row >= r1 && row <= r2 && cell.dataset.date >= s && cell.dataset.date <= e
    })
  }

  #paintSelection() {
    const selected = new Set(this.#selectedCells())
    this.gridTarget.querySelectorAll(".wl-occ-day[data-date]").forEach((cell) => {
      cell.classList.toggle("is-painting", selected.has(cell))
    })
  }

  // --- writes (one POST per touched property) -----------------------------
  async #block({ startDate, endDate, propertyIds }) {
    this.#status("Blocking…")
    const failures = []
    const results = await Promise.all(propertyIds.map((id) => this.#post(id, startDate, endDate)))
    results.forEach((error) => { if (error) failures.push(error) })

    if (failures.length) {
      this.#paintSelection() // anchor is gone — clears the preview
      this.#status(failures[0], true)
      return
    }
    // Full reload: the server re-renders the authoritative grid (fine per spec).
    window.location.reload()
  }

  // Resolves to null on success, an error message on failure.
  async #post(propertyId, startDate, endDate) {
    const url = this.applyUrlsValue[propertyId]
    if (!url) return "Unknown property."
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json",
        },
        body: new URLSearchParams({ start_date: startDate, end_date: endDate, mode: "block" }).toString(),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) return data.error || "Couldn't block those dates."
      return null
    } catch (_e) {
      return "Network error — nothing was changed."
    }
  }

  #status(text, warn = false) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-danger", warn)
    this.statusTarget.classList.toggle("text-muted", !warn)
  }
}
